import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as types from '../types';
import { showRescueGwaVariant } from '../views/RescueLanding/RescueBanner';

interface UsePromoCodeEligibilityProps {
  cart: types.Cart;
  showCoupons: boolean;
}

interface PromoCodeEligible {
  canApplyCode: true;
  ineligibilityReason?: undefined;
}

interface PromoCodeIneligible {
  canApplyCode: false;
  ineligibilityReason: string;
}

export type IndividualPromoCodeEligibility = PromoCodeEligible | PromoCodeIneligible;

interface PromoCodeEligibility {
  couponCodeEligibility: IndividualPromoCodeEligibility;
  giftCardEligibility: IndividualPromoCodeEligibility;
  referralCodeEligibility: IndividualPromoCodeEligibility;
}

export default function usePromoCodeEligibility({
  cart,
  showCoupons,
}: UsePromoCodeEligibilityProps): PromoCodeEligibility {
  const { t } = useTranslation();
  const enableGiftCards = useSelector((state: types.AppState) => !!state.config.siteConfig.enableGiftCards);

  const giftCardEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!enableGiftCards) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('gift_cards_cannot_be_redeemed_at_this_time'),
      };
    }

    return { canApplyCode: true };
  }, [enableGiftCards, t]);

  const referralCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_referral_code_may_not_be_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [showCoupons, t]);

  const couponCodeEligibility: IndividualPromoCodeEligibility = useMemo(() => {
    if (!showCoupons) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('a_coupon_may_not_be_applied_to_this_order'),
      };
    }

    // the rescue slug landing now auto-applies the adoption discount, so the adopter has no code to
    // type and anything they do type can only displace it. only the coupon leg is turned off here:
    // the entry field is a shared omnibox, so gift cards and referrals must keep working.
    // eligibility gate, not the redesign one: the coupon is auto-applied in both arms of a design
    // experiment, so a code typed over it would cost a control adopter the gift just the same
    if (showRescueGwaVariant()) {
      return {
        canApplyCode: false,
        ineligibilityReason: t('the_adoption_promo_is_already_applied_to_this_order'),
      };
    }

    return { canApplyCode: true };
  }, [showCoupons, t]);

  return {
    couponCodeEligibility,
    giftCardEligibility,
    referralCodeEligibility,
  };
}
